import { Day } from "./day_utils";

// The repeat rule a customer can choose on the storefront. Every preset is a
// weekly interval — "Every 4 weeks" is the fourth, never "Monthly" — so a
// recurring booking's dates are always `anchor + 7 * k * interval`, and the
// storefront can filter the server's horizon with plain arithmetic. This
// mirrors `BookingSeries::ALLOWED_RULES` on the backend.
export const RECURRENCE_INTERVALS = [1, 2, 3, 4] as const;
export type RecurrenceInterval = typeof RECURRENCE_INTERVALS[number];

export const RECURRENCE_FREQUENCY = "weekly" as const;

// Below two it is a booking, not a recurring booking
// (`BookingSeries::MIN_OCCURRENCE_COUNT`).
export const MIN_RECURRING_COUNT = 2;

export const DAYS_PER_WEEK = 7;

export interface RecurrenceRule {
  interval: RecurrenceInterval;
  // How many bookings the customer asked for. Unavailable dates on the pattern
  // are skipped and the run extends past them, so this is the number of dates
  // actually booked, not the span in weeks.
  count: number;
}

export function isRecurrenceInterval(
  value: number
): value is RecurrenceInterval {
  return (RECURRENCE_INTERVALS as readonly number[]).includes(value);
}

// Why a window cannot be booked, as the availability endpoint reports it
// (`Bookings::Logic::UnavailabilityReason::REASONS`). One vocabulary covers
// both shapes of refusal: submit names it per lost date on a recurring
// booking, and once for the whole request on an ordinary one.
export type UnavailabilityReason =
  | "closed"
  | "blockout"
  | "min_duration"
  | "lead_time"
  | "elapsed"
  | "cutoff"
  | "booked";

export interface RecurringAvailabilityDate {
  date: Day;
  bookable: boolean;
  reason?: UnavailabilityReason;
}

// One flat, anchor-free answer for a fixed request (the same clock window, or
// the same whole day, repeated): every date from today to the horizon carrying
// a verdict. The picker filters it per preset locally; only changing the time
// window refetches, because the verdict is what changes.
export interface RecurringAvailability {
  spaceId: string;
  horizonEnd: Day;
  durationUnit: "hour" | "day";
  dates: RecurringAvailabilityDate[];
}

export function toRecurringAvailability(data: any): RecurringAvailability {
  return {
    spaceId: data.space_id,
    horizonEnd: data.horizon_end,
    durationUnit: data.duration_unit,
    dates: (data.dates ?? []).map((d: any) => ({
      date: d.date,
      bookable: !!d.bookable,
      reason: d.reason ?? undefined,
    })),
  };
}

// What the quote and the order accept as a booking item's `details.recurrence`.
// `dates` is the storefront's own expansion; the server re-derives the pattern
// from `dates[0]` and refuses any date that is not on it, so nothing here is
// trusted beyond being the customer's choice.
export interface RecurrenceInput {
  frequency: typeof RECURRENCE_FREQUENCY;
  interval: RecurrenceInterval;
  count: number;
  dates: Day[];
  // Why each skipped date on the pattern was skipped, keyed by date — the same
  // verdicts the picker's footnote showed. Advisory: the server keeps a reason
  // only for a date it derives as skipped itself, and only from this
  // vocabulary, so the confirmation and the dashboard can repeat the footnote.
  skippedDateReasons?: Partial<Record<Day, UnavailabilityReason>>;
}
